<template>
  <div class="cms-schema-field">
    <!--fieldsets -->
    <template v-if="fieldsets && fieldsets.length">
      <template v-for="(block, blockIndex) in layoutBlocks">
        <!-- Standalone fields -->
        <v-row v-if="block.type === 'fields'" :key="'b-' + blockIndex" dense>
          <v-col
            v-for="key in block.keys"
            :key="key"
            :cols="getFieldCols(fieldsMap[key])"
          >
            <schema-field-item
              :field="fieldsMap[key]"
              :field-key="key"
              :value="value[key]"
              :sibling-values="value"
              :root-value="rootValue"
              :locales="locales"
              :readonly="readonly"
              :entity-id="entityId"
              @input="onFieldInput(key, $event)"
            />
          </v-col>
        </v-row>

        <!-- Fieldset -->
        <v-expansion-panels
          v-else-if="block.type === 'fieldset' && isFieldsetVisible(block.keys)"
          :key="'fs-' + blockIndex"
          v-model="expandedPanels[block.fieldset.key]"
          accordion
          class="mb-2"
        >
          <v-expansion-panel>
            <v-expansion-panel-header class="subtitle-2">
              {{ block.fieldset.label || block.fieldset.key }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row dense>
                <v-col
                  v-for="key in block.keys"
                  :key="key"
                  :cols="getFieldCols(fieldsMap[key])"
                >
                  <schema-field-item
                    :field="fieldsMap[key]"
                    :field-key="key"
                    :value="value[key]"
                    :sibling-values="value"
                    :root-value="rootValue"
                    :locales="locales"
                    :readonly="readonly"
                    :entity-id="entityId"
                    @input="onFieldInput(key, $event)"
                  />
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
    </template>

    <!-- no fieldsets  -->
    <v-row v-else dense>
      <v-col
        v-for="key in flatFieldKeys"
        :key="key"
        :cols="getFieldCols(fieldsMap[key])"
      >
        <schema-field-item
          :field="fieldsMap[key]"
          :field-key="key"
          :value="value[key]"
          :sibling-values="value"
          :root-value="rootValue"
          :locales="locales"
          :readonly="readonly"
          :entity-id="entityId"
          @input="onFieldInput(key, $event)"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import SchemaFieldItem from './SchemaFieldItem.vue'
import fieldVisibility from '../mixins/fieldVisibility'

const SIMPLE_FIELD_TYPES = [
  'string',
  'text',
  'integer',
  'number',
  'boolean',
  'array:int',
  'array:string',
]

export default {
  name: 'SchemaField',
  components: {
    SchemaFieldItem,
  },
  mixins: [fieldVisibility],
  props: {
    fields: {
      type: [Object, Array],
      required: true,
    },
    value: {
      type: Object,
      default: () => ({}),
    },
    rootValue: {
      type: Object,
      required: true,
    },
    fieldsets: {
      type: Array,
      default: null,
    },
    locales: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    entityId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      expandedPanels: {},
    }
  },
  computed: {
    fieldsMap() {
      if (Array.isArray(this.fields)) {
        const map = {}
        for (const field of this.fields) {
          if (field.key) {
            map[field.key] = field
          }
        }
        return map
      }
      return this.fields
    },
    layoutBlocks() {
      const fieldsetByKey = Object.fromEntries((this.fieldsets ?? []).map((fs) => [fs.key, fs]))
      const blocks = []
      const fieldsetBlocks = {}
      let standaloneKeys = []
      for (const [key, field] of Object.entries(this.fieldsMap)) {
        const fieldset = fieldsetByKey[field.fieldset]
        if (!fieldset) {
          standaloneKeys.push(key)
          continue
        }
        if (standaloneKeys.length) {
          blocks.push({ type: 'fields', keys: standaloneKeys })
          standaloneKeys = []
        }
        if (fieldsetBlocks[field.fieldset]) {
          fieldsetBlocks[field.fieldset].keys.push(key)
        } else {
          const block = { type: 'fieldset', fieldset, keys: [key] }
          blocks.push(block)
          fieldsetBlocks[field.fieldset] = block
        }
      }
      if (standaloneKeys.length) {
        blocks.push({ type: 'fields', keys: standaloneKeys })
      }
      return blocks
    },
    flatFieldKeys() {
      const keys = Object.keys(this.fieldsMap)
      return keys.sort((a, b) => {
        const aSimple = this.isSimpleField(this.fieldsMap[a])
        const bSimple = this.isSimpleField(this.fieldsMap[b])
        if (aSimple === bSimple) {
          return 0
        }
        return aSimple ? -1 : 1
      })
    },
  },
  created() {
    if (this.fieldsets && this.fieldsets.length) {
      const panels = {}
      for (const fieldset of this.fieldsets) {
        if (fieldset.expanded) {
          panels[fieldset.key] = 0
        }
      }
      this.expandedPanels = panels
    }
  },
  methods: {
    onFieldInput(key, val) {
      const updated = cloneDeep(this.value || {})
      updated[key] = val
      this.$emit('input', updated)
    },
    isFieldsetVisible(fieldKeys) {
      if (!fieldKeys.length) {
        return false
      }
      return fieldKeys.some((key) => {
        const field = this.fieldsMap[key]
        return this.matchesDependsOn(field && field.depends_on)
      })
    },
    isSimpleField(field) {
      return !field || SIMPLE_FIELD_TYPES.includes(field.type)
    },
    getFieldCols(field) {
      if (!field) {
        return 12
      }
      if (field.cols) {
        return field.cols
      }
      if (field.type === 'boolean') {
        return 2
      }
      if (field.type === 'number' || field.type === 'integer') {
        return 6
      }
      return 12
    },
  },
}
</script>
